import { useMemo } from "react";
import { getEventModelName } from "@/entities/data-model";
import {
    EventAssertion,
    type EventFieldsGroup,
    GroupType,
    type HierarchicalFieldGroup,
    ResponseItemType,
    useMetadataQuery,
} from "@/entities/form-metadata";

export interface EventExactnessConfig {
    /** Set of event keys (e.g. "SelfBirth") where Year has showExactness: false */
    yearDisabled: Set<string>;
    /** Set of event keys where Place has showExactness: false */
    placeDisabled: Set<string>;
}

export const useEventFieldsWithExactnessDisabled = (): EventExactnessConfig => {
    const { data: formMetadata } = useMetadataQuery();

    return useMemo(() => {
        const yearDisabled = new Set<string>();
        const placeDisabled = new Set<string>();

        for (const entry of formMetadata.fieldGroupsAndFeatureContainers) {
            if (entry.type !== ResponseItemType.FieldGroup) continue;

            if (entry.groupType === GroupType.Event) {
                const eventGroup = entry as EventFieldsGroup;
                for (const fieldset of eventGroup.fieldsets) {
                    const eventKey = getEventModelName({
                        eventType: fieldset.eventType,
                        relation: fieldset.relation,
                    });
                    if (!eventKey) continue;
                    for (const field of fieldset.fields) {
                        if (field.assertion === EventAssertion.Year && field.features.showExactness === false) {
                            yearDisabled.add(eventKey);
                        }
                        if (field.assertion === EventAssertion.Place && field.features.showExactness === false) {
                            placeDisabled.add(eventKey);
                        }
                    }
                }
            }

            if (entry.groupType === GroupType.Hierarchical) {
                // Hierarchical groups only expose place fields (e.g. country → state → city),
                // so they can only ever contribute to placeDisabled — never yearDisabled.
                const hierarchicalGroup = entry as HierarchicalFieldGroup;
                for (const hierarchy of hierarchicalGroup.hierarchies) {
                    const eventKey = getEventModelName({
                        eventType: hierarchy.eventType,
                        relation: hierarchy.relation,
                    });
                    if (!eventKey) continue;
                    for (const field of hierarchy.fields) {
                        if (field.features.showExactness === false) {
                            placeDisabled.add(eventKey);
                        }
                    }
                }
            }
        }

        return { yearDisabled, placeDisabled };
    }, [formMetadata.fieldGroupsAndFeatureContainers]);
};
